<script setup>
import { computed } from 'vue'
import { getFormColor } from '../../../composables/useReservationsCalendar.js'
import { trans } from 'laravel-vue-i18n'

const props = defineProps({
    reservations:   { type: Array, default: () => [] },
    visibleFormIds: { type: Set,   default: () => new Set() },
})

const emit = defineEmits(['open-reservation', 'new-reservation'])

// ─── Dnešní datum ────────────────────────────────────────────────

const now = new Date()
const todayStr = now.toISOString().slice(0, 10)   // 'YYYY-MM-DD'

const DOW_LONG = [
    trans('common.dow_long.sun'),
    trans('common.dow_long.mon'),
    trans('common.dow_long.tue'),
    trans('common.dow_long.wed'),
    trans('common.dow_long.thu'),
    trans('common.dow_long.fri'),
    trans('common.dow_long.sat'),
]
const MONTHS_GEN = [
    trans('common.months_gen.jan'),
    trans('common.months_gen.feb'),
    trans('common.months_gen.mar'),
    trans('common.months_gen.apr'),
    trans('common.months_gen.may'),
    trans('common.months_gen.jun'),
    trans('common.months_gen.jul'),
    trans('common.months_gen.aug'),
    trans('common.months_gen.sep'),
    trans('common.months_gen.oct'),
    trans('common.months_gen.nov'),
    trans('common.months_gen.dec'),
]

const todayLabel = computed(() => {
    const dow   = DOW_LONG[now.getDay()]
    const day   = now.getDate()
    const month = MONTHS_GEN[now.getMonth()]
    return `${dow} ${day}. ${month}`
})

// ─── Dnešní rezervace ────────────────────────────────────────────

const todayReservations = computed(() =>
    props.reservations
        .filter(r => {
            if (!props.visibleFormIds.has(r.id_reservation_forms)) return false
            return r.date === todayStr
        })
        .sort((a, b) => (a.time_start ?? '').localeCompare(b.time_start ?? ''))
)

// Karty: max 4 rezervací + doplnit prázdnými sloty na 4
const MAX_CARDS = 4
const emptyCount = computed(() =>
    Math.max(0, MAX_CARDS - todayReservations.value.length)
)

// ─── Helpers ─────────────────────────────────────────────────────

function fmtTime(t) {
    return t ? t.substring(0, 5) : ''
}

function timeRange(r) {
    if (!r.time_start) return null
    return `${fmtTime(r.time_start)}–${fmtTime(r.time_end)}`
}

function cardColor(r) {
    return getFormColor(r.reservation_forms)
}

function statusShort(state) {
    if (state === 'accepted') return trans('reservations.state_accepted_short')
    if (state === 'pending')  return trans('reservations.state_pending_label')
    if (state === 'rejected') return trans('reservations.state_rejected_label')
    return ''
}
</script>

<template>
    <div class="px-5 py-3.5 bg-white dark:bg-neutral-900 border-b border-neutral-100 dark:border-neutral-800 flex-shrink-0 max-md:hidden">

        <!-- Hlavička -->
        <div class="flex items-baseline justify-between gap-2 mb-3">
            <h3 class="text-sm font-semibold text-neutral-800 dark:text-neutral-100 leading-none">
                {{ $t('common.today') }} · {{ todayLabel }}
                <small class="text-[12px] font-medium text-neutral-400 dark:text-neutral-500 ml-2 tracking-wide uppercase">
                    {{ todayReservations.length }} {{ $t('reservations.cal_res_suffix') }}
                </small>
            </h3>
        </div>

        <!-- Karty -->
        <div class="grid grid-cols-4 gap-2">

            <!-- Rezervace -->
            <div
                v-for="r in todayReservations.slice(0, MAX_CARDS)"
                :key="r.id"
                class="today-card"
                :style="{ '--c': cardColor(r), borderLeftColor: cardColor(r) }"
                :class="{ 'today-card--pending': r.state === 'pending' }"
                @click="emit('open-reservation', r)"
            >
                <!-- Čas -->
                <div class="today-card-time">
                    {{ timeRange(r) ?? r.formatted_date }}
                </div>

                <!-- Název -->
                <div class="today-card-title">{{ r.name }}</div>

                <!-- Formulář -->
                <div class="today-card-sub">
                    <span
                        class="w-1.5 h-1.5 rounded-full flex-shrink-0"
                        :style="{ background: cardColor(r) }"
                    />
                    {{ r.reservation_forms?.name }}
                </div>

                <!-- Status badge (absolutní, vpravo nahoře) -->
                <span
                    class="today-card-status"
                    :class="{
                        'text-amber-600':   r.state === 'pending',
                        'text-[var(--c)]':  r.state !== 'pending',
                    }"
                >
                    {{ statusShort(r.state) }}
                </span>
            </div>

            <!-- Prázdné sloty -->
            <div
                v-for="i in emptyCount"
                :key="'empty-' + i"
                class="today-card today-card--empty"
                @click="emit('new-reservation')"
            >
                <i class="pi pi-plus !text-base text-neutral-300 mb-0.5"/>
                <span class="text-[12px] text-neutral-300 leading-tight text-center">{{ $t('reservations.today_strip_empty_slot') }}</span>
            </div>
        </div>
    </div>
</template>

<style scoped>
.today-card {
    background: white;
    border: 1px solid #EBEBED;
    border-left: 3px solid var(--c, #9BA1AB);
    border-radius: 10px;
    padding: 10px 12px;
    display: flex;
    flex-direction: column;
    gap: 3px;
    position: relative;
    cursor: pointer;
    transition: transform .12s, box-shadow .12s;
    min-width: 0;
}

.today-card:hover {
    transform: translateY(-1px);
    box-shadow: 0 6px 20px -8px rgba(15,17,21,.18);
}

.today-card--empty {
    border: 1px dashed #D4D4D8 !important;
    border-left: 1px dashed #D4D4D8 !important;
    border-radius: 10px;
    align-items: center;
    justify-content: center;
    cursor: pointer;
}

.today-card--empty:hover {
    background: #FAFAFA;
}

.today-card-time {
    font-size: 11px;
    font-weight: 600;
    color: #3A3F47;
    letter-spacing: .03em;
    font-variant-numeric: tabular-nums;
}

.today-card-title {
    font-size: 13px;
    font-weight: 500;
    color: #0F1115;
    line-height: 1.3;
    white-space: nowrap;
    overflow: hidden;
    text-overflow: ellipsis;
    padding-right: 42px; /* prostor pro status badge */
}

.today-card-sub {
    display: flex;
    align-items: center;
    gap: 5px;
    font-size: 11px;
    color: #9BA1AB;
    white-space: nowrap;
    overflow: hidden;
    text-overflow: ellipsis;
}

.today-card-status {
    position: absolute;
    top: 10px;
    right: 10px;
    font-size: 9px;
    font-weight: 700;
    letter-spacing: .06em;
    text-transform: uppercase;
}

/* Dark mode */
:root.dark .today-card {
    background: #1c1c1e;
    border-color: #2a2a2e;
}

:root.dark .today-card--empty {
    border-color: #3a3a3e !important;
}

:root.dark .today-card--empty:hover {
    background: #252528;
}

:root.dark .today-card-time {
    color: #b0b8c4;
}

:root.dark .today-card-title {
    color: #f0f0f2;
}

:root.dark .today-card-sub {
    color: #6b737e;
}
</style>
